import React, { useMemo } from "react";
import { RecoilRoot } from "recoil";
import { allCCTState, newMissionState } from "../../../atoms/core";
import { MockedProvider } from "@apollo/client/testing";
import CreateMissionSalary from "../../../screens/create-mission/CreateMissionSalary";
import {
    EarningType,
    GetSalaryDetailsQuery,
    ProfileType,
    Repetition,
    Unit,
} from "../../../__generated__/graphql";
import VStack from "../../../ui_atoms/VStack";
import type { Meta, StoryObj } from "@storybook/react";
import {
    buildCct,
    buildClient,
    buildClientInfo,
    buildClientProfile,
    buildContract,
    buildIjmGroup,
    buildMission,
    buildUser,
    buildWorkerInfo,
    IJM_CHOICES_MOCK,
} from "../../factories";
import { buildGraphqlMock_GET_SALARY_DETAILS } from "../../mock-factories";
import { CardStyleInterpolators, createStackNavigator } from "@react-navigation/stack";
import SalaryCountingDetailsScreen from "../../../screens/SalaryCountingDetails";
import { addDays } from "date-fns";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import CreateMissionManager from "../../../contexts/CreateMissionManager";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import IJMEditorNavScreen from "../../../screens/client/IJMEditorNavScreen";

type StoryStackParams = {
    CreateMissionSalary: undefined;
    IJMEditorNavScreen: undefined;
    SalaryCountingDetails: {
        earningType: EarningType;
        salaryDetails: Pick<
            GetSalaryDetailsQuery["getSalaryDetails"],
            | "workerDeductions"
            | "invoiceSalary"
            | "netSalary"
            | "lseMargin"
            | "grossSalary"
            | "vacationCompensation"
            | "publicHolidaysCompensation"
            | "thirteenMonthCompensation"
            | "baseSalary"
            | "clientDeductions"
        >;
    };
};

const Stack = createStackNavigator<StoryStackParams>();

const WORKING_TIME_CLASSIC_SPEC = {
    [EarningType.Hourly]: {
        unit: Unit.Hours,
        unitCount: 10,
        repetition: Repetition.Week2,
    },
    [EarningType.Daily]: {
        unit: Unit.Days,
        unitCount: 5,
        repetition: Repetition.Month,
    },
    [EarningType.Monthly]: {
        unit: Unit.Hours,
        unitCount: 21,
        repetition: Repetition.Week,
    },
    [EarningType.Package]: {
        unit: Unit.Hours,
        unitCount: 32,
        repetition: Repetition.None,
    },
};

const SALARIES = {
    [EarningType.Hourly]: 3500,
    [EarningType.Daily]: 45000,
    [EarningType.Monthly]: 800000,
    [EarningType.Package]: 300000,
};

function StoryComponent({
    earningType,
    duration,
    hasIjm,
    hasSalary = true,
    canChangeIjmGroup = false,
}: {
    earningType: EarningType;
    duration: number;
    hasIjm: boolean;
    hasSalary: boolean;
    canChangeIjmGroup: boolean;
}) {
    const mocks = useMemo(() => [buildGraphqlMock_GET_SALARY_DETAILS(), IJM_CHOICES_MOCK], []);
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            ijmGroup: buildIjmGroup({
                                id: "GM-G1-730-2",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 10,
                            canChangeIjmGroup,
                        }),
                    }),
                ],
            }),
        [canChangeIjmGroup],
    );
    const ijmGroup = useMemo(
        () =>
            buildIjmGroup({
                id: "GM-G1-730-2",
            }),
        [],
    );

    const mission = useMemo(
        () =>
            buildMission({
                workerInfo: buildWorkerInfo({
                    id: "worker",
                    birthdate: "1975-05-12",
                }),
                currentContract: buildContract({
                    earningType: earningType,
                    startDate: new Date().toISOString(),
                    endDate: addDays(new Date(), duration).toISOString(),
                    workingTimeClassicSpec: {
                        ...WORKING_TIME_CLASSIC_SPEC[earningType],
                        //unitCount: 42,
                    },
                    grossSalary: hasSalary ? SALARIES[earningType] : null,
                    hoursPerWeekFullTime: 42,
                    ijmInfo: hasIjm
                        ? {
                              ijmGroup: ijmGroup,
                              payFirstDays: false,
                              payRemainingPercents: 10,
                          }
                        : null,
                }),
            }),
        [duration, earningType, hasIjm, hasSalary, ijmGroup],
    );
    const cctList = useMemo(
        () => [
            buildCct({
                name: "Super CCT",
            }),
        ],
        [],
    );
    return (
        <RecoilRoot
            initializeState={(snap) => {
                snap.set(newMissionState, mission);
                snap.set(allCCTState, cctList);
            }}
        >
            <MockedProvider mocks={mocks}>
                <MockedAuthManager user={user}>
                    <MockedProfileSwitcherManager
                        profileType={ProfileType.Client}
                        profileId={ProfileType.Client}
                    >
                        <CreateMissionManager
                            testingInitializedData={{
                                ...mission,
                                ijmSelection: hasIjm
                                    ? {
                                          ijmGroupId: ijmGroup.id,
                                          ijmGroupName: ijmGroup.name,
                                          ijmPayFirstDays: false,
                                          ijmPayRemainingPercents: 10,
                                      }
                                    : {},
                            }}
                        >
                            <VStack
                                gap={1}
                                style={{ alignItems: "stretch", alignSelf: "stretch", flex: 1 }}
                            >
                                <Stack.Navigator initialRouteName="CreateMissionSalary">
                                    <Stack.Screen
                                        name="CreateMissionSalary"
                                        component={CreateMissionSalary}
                                        options={{
                                            gestureEnabled: false,
                                            headerShown: true,
                                            headerBackButtonDisplayMode: "default",
                                        }}
                                    />
                                    <Stack.Screen
                                        name="SalaryCountingDetails"
                                        component={SalaryCountingDetailsScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            cardStyleInterpolator:
                                                CardStyleInterpolators.forVerticalIOS,
                                        }}
                                    />
                                    <Stack.Screen
                                        name="IJMEditorNavScreen"
                                        component={IJMEditorNavScreen}
                                        options={{
                                            headerShown: false,
                                            gestureEnabled: true,
                                            animation: "slide_from_bottom",
                                        }}
                                    />
                                </Stack.Navigator>
                            </VStack>
                        </CreateMissionManager>
                    </MockedProfileSwitcherManager>
                </MockedAuthManager>
            </MockedProvider>
        </RecoilRoot>
    );
}

const meta = {
    title: "CreateMission/Salary",
    component: StoryComponent,
    argTypes: {
        earningType: {
            options: [EarningType.Hourly, EarningType.Monthly],
            control: { type: "radio" },
        },
        duration: {
            options: [1, 30],
            value: {
                defaultValue: 30,
            },
            control: { type: "radio" },
        },
    },
    args: {
        canChangeIjmGroup: true,
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Hourly: Story = {
    args: {
        earningType: EarningType.Hourly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const Monthly: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const Daily: Story = {
    args: {
        earningType: EarningType.Daily,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const Package: Story = {
    args: {
        earningType: EarningType.Package,
        duration: 30,
        hasIjm: true,
        hasSalary: true,
    },
};

export const PackageShort: Story = {
    args: {
        earningType: EarningType.Package,
        duration: 0,
        hasIjm: true,
        hasSalary: true,
        canChangeIjmGroup: false,
    },
};

export const MonthlyNoIjm: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 0,
        hasSalary: true,
        hasIjm: false,
    },
};

export const MonthlyEmpty: Story = {
    args: {
        earningType: EarningType.Monthly,
        duration: 0,
        hasIjm: false,
        hasSalary: false,
    },
};
